import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Button, Dropdown, Image, Upload } from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { MoreVert } from "@mui/icons-material";
import * as yup from "yup";
import { useToast } from "@chakra-ui/react";
import useAxios from "../../utils/useAxios";
import { canonicalAssetTypeName } from "../../utils/assetTypeName";
import EditComponentScopeDialog from "./EditComponentScopeDialog";
import { MAX_GALLERY_IMAGES } from "../../config/uploadLimits";
import {
  createLimitedBeforeUpload,
  getImageUploadLimitState,
} from "../../utils/imageUploadLimit";
import UploadingImageTile from "../ui/UploadingImageTile";
import EquipmentAutocomplete from "../Assets/EquipmentAutocomplete";

const componentSchema = yup.object().shape({
  tagNumber: yup.string().required("Tag Number is required"),
  prevTagNumber: yup.string(),
  componentName: yup.string(),
  description: yup.string(),
});

// Scoped edit propagation — apply an edit to every matching component
// across tiers/plant, not just the one being edited — is fully implemented
// (edit-scope-preview/apply-scoped-edit endpoints, EditComponentScopeDialog
// below) but temporarily disabled. Edits apply only to the selected
// component, like before this feature existed. Flip this to true to
// re-enable the scope-choice step — no other changes needed.
const SCOPED_EDIT_PROPAGATION_ENABLED = false;

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const getResultsArray = (data) => {
  const normalized = Array.isArray(data) ? data : data?.results || [];
  return Array.isArray(normalized) ? normalized : [];
};

// `plantId` selects the new, server-searched picker (Plants.jsx, Logistics);
// callers that still pass a full `assets` list (Sites.jsx, Equipment.jsx,
// Instruments.jsx, PanelComponents.jsx - each already loads its own, out of
// this task's scope) keep the original client-filtered one unchanged.
export default function EditComponent({
  component,
  open,
  onClose,
  onSuccess,
  assets,
  plantId,
}) {
  const api = useAxios();
  const toast = useToast();
  const safeAssets = Array.isArray(assets) ? assets : [];

  const [tagNumber, setTagNumber] = useState("");
  const [prevTagNumber, setPrevTagNumber] = useState("");
  const [componentName, setComponentName] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [partNumber, setPartNumber] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [relatedAssets, setRelatedAssets] = useState([]);
  const [assetType, setAssetType] = useState(null);
  const [assetTypes, setAssetTypes] = useState([]);
  const [flags, setFlags] = useState([]);

  // Scoped-edit propagation: after validating, we preview how many other
  // rows represent the same physical component (same tagNumber+description,
  // see /panel-components/{id}/edit-scope-preview/) before asking how far
  // to apply the edit.
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [scopePreview, setScopePreview] = useState(null);
  const [scopeChoice, setScopeChoice] = useState("self");
  const [isApplying, setIsApplying] = useState(false);

  // Images
  const [componentImages, setComponentImages] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [uploadingImages, setUploadingImages] = useState([]);

  // Populate fields when component changes
  useEffect(() => {
    if (!component) return;

    setTagNumber(component.tagNumber ?? "");
    setPrevTagNumber(component.prevTagNumber ?? "");
    setComponentName(component.componentName ?? "");
    setSerialNumber(component.serialNumber ?? "");
    setPartNumber(component.partNumber ?? "");
    setDescription(component.description ?? "");
    setAssetType(component.assetType ?? null);
    setErrors({});
    setFileList([]);
    setRelatedAssets(component.relatedTo ?? []);
    setScopeDialogOpen(false);
    setScopePreview(null);
    setScopeChoice("self");

    api
      .get(`/component-images/?component=${component.id}`)
      .then((res) => setComponentImages(getResultsArray(res.data)))
      .catch((err) => console.error("Failed to load component images", err));

    api
      .get(`/panel-components/${component.id}/`)
      .then((res) => setFlags(res.data.flags || []))
      .catch((err) => console.error("Failed to load component flags", err));
  }, [api, component?.id]);

  // Fetch asset types only when the modal is opened.
  useEffect(() => {
    if (!open) return;

    api
      .get("/asset-types/")
      .then((res) => setAssetTypes(getResultsArray(res.data)))
      .catch((err) => console.error("Error fetching asset types:", err));
  }, [api, open]);

  const handleClose = () => {
    setErrors({});
    setFileList([]);
    setScopeDialogOpen(false);
    setScopePreview(null);
    setScopeChoice("self");
    onClose();
  };

  // Restores the pre-propagation behaviour: validate, then PATCH just this
  // component. Used while SCOPED_EDIT_PROPAGATION_ENABLED is false.
  const handleSubmitSingle = async () => {
    const formData = new FormData();
    formData.append("tagNumber", tagNumber);
    formData.append("prevTagNumber", prevTagNumber);
    formData.append("componentName", componentName);
    formData.append("serialNumber", serialNumber);
    // Empty clears it: the API stores a blank part number as null.
    formData.append("partNumber", partNumber);
    formData.append("description", description);
    if (assetType) formData.append("assetType", assetType);
    relatedAssets.forEach((a) => formData.append("relatedTo", a.id));

    const response = await api({
      method: "PATCH",
      url: `/panel-components/${component.id}/`,
      data: formData,
    });
    // Show the stored (normalised) part number when the form is opened again.
    if (response?.data && "partNumber" in response.data) {
      setPartNumber(response.data.partNumber ?? "");
    }

    toast({
      title: "Success",
      description: "Component details updated successfully!",
      status: "success",
      duration: 4000,
      isClosable: true,
      position: "bottom",
    });

    onSuccess?.();
    handleClose();
  };

  // Validates, then — when scoped-edit propagation is enabled — previews
  // how many other rows represent the same physical component before
  // asking how far to apply the edit (handleConfirmApply does the actual
  // PATCH once a scope is chosen). While disabled, applies directly to
  // just this component via handleSubmitSingle above.
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await componentSchema.validate(
        { tagNumber, prevTagNumber, componentName, description },
        { abortEarly: false },
      );
      setErrors({});

      if (!SCOPED_EDIT_PROPAGATION_ENABLED) {
        await handleSubmitSingle();
        return;
      }

      const { data } = await api.get(
        `/panel-components/${component.id}/edit-scope-preview/`,
      );
      setScopePreview(data);
      setScopeChoice("self");
      setScopeDialogOpen(true);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const newErrors = {};
        error.inner.forEach((e) => {
          newErrors[e.path] = e.message;
        });
        setErrors(newErrors);
      } else {
        toast({
          title: "Error",
          description: SCOPED_EDIT_PROPAGATION_ENABLED
            ? "Could not check for related components. Please try again."
            : "Failed to update component details.",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmApply = async () => {
    setIsApplying(true);
    try {
      const payload = {
        tagNumber,
        prevTagNumber,
        componentName,
        serialNumber,
        partNumber,
        description,
        assetType,
        relatedTo: relatedAssets.map((a) => a.id),
      };

      const { data } = await api.post(
        `/panel-components/${component.id}/apply-scoped-edit/`,
        { scope: scopeChoice, payload },
      );

      const results = data?.results ?? [];
      const updatedCount = results.filter((r) => r.status === "updated").length;
      const skippedCount = results.filter((r) => r.status === "skipped").length;

      toast({
        title: updatedCount > 0 ? "Component(s) updated" : "Nothing updated",
        description:
          `${updatedCount} updated` +
          (skippedCount ? `, ${skippedCount} skipped` : "") +
          ".",
        status: updatedCount > 0 ? "success" : "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });

      onSuccess?.();
      handleClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update component details.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setIsApplying(false);
    }
  };

  // "Card" flags this component for the Risk Assets "card" category
  // (risk_assets_services.py) -- a plain add/remove of a Flag row, same
  // shape as the "drive" flag the Tier form still offers.
  const handleCardFlagToggle = async (checked) => {
    const cardFlag = flags.find((flag) => flag.name === "card");

    try {
      if (checked) {
        const res = await api.post("/flags/", { panel_component: component.id, name: "card" });
        setFlags((prev) => [...prev, res.data]);
      } else if (cardFlag) {
        await api.delete(`/flags/${cardFlag.id}/`);
        setFlags((prev) => prev.filter((flag) => flag.id !== cardFlag.id));
      }
    } catch (error) {
      const isForbidden = error?.response?.status === 403;
      toast({
        title: "Error",
        description: isForbidden
          ? "You don't have permission to change flags."
          : "Failed to update the Card flag.",
        status: "error", duration: 4000, isClosable: true, position: "bottom",
      });
    }
  };

  const createAssetType = async (name) => {
    try {
      const res = await api.post("/asset-types/", { name: canonicalAssetTypeName(name) });
      const newType = res.data;
      setAssetTypes((prev) => [...prev, newType]);
      return newType;
    } catch (error) {
      console.error("Error creating asset type:", error);
      return null;
    }
  };

  const handleDeleteImage = async (id) => {
    try {
      await api.delete(`/component-images/${id}/`);
      setComponentImages((prev) =>
        Array.isArray(prev) ? prev.filter((img) => img.id !== id) : [],
      );
    } catch (err) {
      console.error("Failed to delete image:", err);
    }
  };

  const handleDownloadImage = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = "image.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const uploadImage = async (file) => {
    const uid = crypto.randomUUID();
    setUploadingImages((prev) => [...prev, { uid, progress: 0 }]);

    const formData = new FormData();
    formData.append("component", component.id);
    formData.append("image", file);

    try {
      const res = await api.post("/component-images/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          const progress = event.total ? Math.round((event.loaded * 100) / event.total) : 0;
          setUploadingImages((prev) => prev.map((u) => (u.uid === uid ? { ...u, progress } : u)));
        },
      });
      setComponentImages((prev) =>
        Array.isArray(prev) ? [...prev, res.data] : [res.data],
      );
    } finally {
      setUploadingImages((prev) => prev.filter((u) => u.uid !== uid));
    }
  };

  const fields = [
    {
      label: "Tag Number",
      value: tagNumber,
      setter: setTagNumber,
      errorKey: "tagNumber",
      required: true,
    },
    {
      label: "Previous Tag Number",
      value: prevTagNumber,
      setter: setPrevTagNumber,
      errorKey: "prevTagNumber",
      required: false,
    },
    {
      label: "Name",
      value: componentName,
      setter: setComponentName,
      errorKey: "componentName",
      required: false,
    },
    {
      label: "Serial Number",
      value: serialNumber,
      setter: setSerialNumber,
      errorKey: "serialNumber",
      required: false,
    },
    {
      label: "Part Number",
      value: partNumber,
      setter: setPartNumber,
      errorKey: "partNumber",
      required: false,
      // The API stores part numbers normalised.
      hint: "Saved without spaces, in capitals",
    },
    {
      label: "Description",
      value: description,
      setter: setDescription,
      errorKey: "description",
      required: false,
    },
  ];

  const { canUploadMore, remainingSlots, limitLabel } = getImageUploadLimitState(
    componentImages.length + uploadingImages.length,
    MAX_GALLERY_IMAGES,
  );

  return (
    <>
    <Modal open={open && !scopeDialogOpen} onClose={handleClose} sx={{ zIndex: 1000 }}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", md: 500 },
          bgcolor: "background.paper",
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          borderRadius: "20px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5,
            bgcolor: "primary.main",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}
          >
            Update Component
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#fff",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseOutlined />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          sx={{
            overflowY: "auto",
            flexGrow: 1,
            px: 3,
            py: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "grey.400",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "grey.600" },
            "&::-webkit-scrollbar-track": { backgroundColor: "grey.100" },
          }}
        >
          {/* Text fields */}
          <Grid container spacing={2}>
            {fields.map(
              ({ label, value, setter, errorKey, required, hint }, index) => (
                <Grid item xs={index < 4 ? 6 : 12} key={label}>
                  <TextField
                    label={label}
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    variant="outlined"
                    fullWidth
                    required={required}
                    error={!!errors[errorKey]}
                    helperText={errors[errorKey] || hint}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                  />
                </Grid>
              ),
            )}
          </Grid>
          {/* Parent Assets */}
          {plantId ? (
            <EquipmentAutocomplete
              multiple
              plantId={plantId}
              excludeId={component?.id}
              value={relatedAssets}
              onChange={setRelatedAssets}
              label="Parent Assets"
            />
          ) : (
            <Autocomplete
              multiple
              options={safeAssets.filter((a) => a.id !== component?.id)}
              getOptionLabel={(option) =>
                `${option.tagNumber} - ${option.equipmentName}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={relatedAssets}
              onChange={(_, selected) => setRelatedAssets(selected)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Assets"
                  variant="outlined"
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              )}
            />
          )}

          {/* Asset type */}
          <Autocomplete
            options={assetTypes}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={assetTypes.find((t) => t.id === assetType) || null}
            filterOptions={(options, params) => {
              const filtered = options.filter((o) =>
                o.name.toLowerCase().includes(params.inputValue.toLowerCase()),
              );
              if (params.inputValue.trim() !== "" && filtered.length === 0) {
                filtered.push({
                  id: "__new__",
                  name: `Add "${canonicalAssetTypeName(params.inputValue)}"`,
                  inputValue: canonicalAssetTypeName(params.inputValue),
                });
              }
              return filtered;
            }}
            onChange={async (_, selected) => {
              if (!selected) return setAssetType(null);
              if (selected.id === "__new__") {
                const created = await createAssetType(selected.inputValue);
                if (created) setAssetType(created.id);
              } else {
                setAssetType(selected.id);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Asset Type"
                variant="outlined"
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
            )}
          />

          {/* Flags */}
          <FormControlLabel
            control={(
              <Checkbox
                checked={flags.some((flag) => flag.name === "card")}
                onChange={(event) => handleCardFlagToggle(event.target.checked)}
              />
            )}
            label="Card"
          />

          {/* Images */}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.8,
              color: "primary.main",
            }}
          >
            Images
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "grey.50",
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {componentImages.map((img) => (
              <Box
                key={img.id}
                sx={{
                  position: "relative",
                  width: 120,
                  height: 120,
                  borderRadius: "10px",
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
                <Image
                  src={img.image_url}
                  width={120}
                  height={120}
                  style={{ objectFit: "cover" }}
                />
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "delete",
                        label: "Delete",
                        icon: <DeleteOutlined style={{ color: "red" }} />,
                        onClick: () => handleDeleteImage(img.id),
                      },
                      {
                        key: "download",
                        label: "Download Image",
                        icon: <DownloadOutlined />,
                        onClick: () => handleDownloadImage(img.image_url),
                      },
                    ],
                  }}
                  trigger={["click"]}
                  placement="bottomRight"
                  overlayStyle={{ zIndex: 3000 }}
                  arrow
                >
                  <IconButton
                    sx={{
                      position: "absolute",
                      bottom: 4,
                      right: 4,
                      bgcolor: "rgba(255,255,255,0.9)",
                      borderRadius: "50%",
                      p: 0.5,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      "&:hover": { bgcolor: "#fff" },
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Dropdown>
              </Box>
            ))}

            {uploadingImages.map((u) => (
              <UploadingImageTile key={u.uid} size={120} progress={u.progress} />
            ))}

            {canUploadMore && (
              <Upload
                listType="picture-card"
                capture="environment"
                fileList={fileList}
                onPreview={handlePreview}
                onChange={({ fileList: newList }) => setFileList(newList)}
                maxCount={remainingSlots}
                beforeUpload={createLimitedBeforeUpload({
                  getCurrentCount: () => componentImages.length + uploadingImages.length,
                  limit: MAX_GALLERY_IMAGES,
                  onLimitExceeded: () =>
                    toast({
                      title: "Limit reached",
                      description: `You can upload up to ${MAX_GALLERY_IMAGES} images.`,
                      status: "warning",
                      duration: 4000,
                      isClosable: true,
                      position: "bottom",
                    }),
                })}
                multiple
                customRequest={async ({ file, onSuccess, onError }) => {
                  try {
                    await uploadImage(file);
                    onSuccess("ok");
                  } catch (err) {
                    onError(err);
                  }
                }}
                showUploadList={false}
              >
                <button style={{ border: 0, background: "none" }} type="button">
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>
                    <div>Upload</div>
                    {limitLabel && <div>{limitLabel}</div>}
                  </div>
                </button>
              </Upload>
            )}
          </Box>

          {previewImage && (
            <Image
              wrapperStyle={{ display: "none" }}
              preview={{
                visible: previewOpen,
                zIndex: 2000,
                onVisibleChange: (v) => setPreviewOpen(v),
                afterOpenChange: (v) => !v && setPreviewImage(""),
              }}
              src={previewImage}
            />
          )}

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              mt: 1,
            }}
          >
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={isLoading}
              style={{ minWidth: 120 }}
            >
              {isLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Update"
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
    <EditComponentScopeDialog
      open={open && scopeDialogOpen}
      preview={scopePreview}
      scope={scopeChoice}
      onScopeChange={setScopeChoice}
      onBack={() => setScopeDialogOpen(false)}
      onConfirm={handleConfirmApply}
      submitting={isApplying}
    />
    </>
  );
}
